import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createCheckoutSession, getCheckout, updateCheckout, confirmCheckout, waitForCheckoutResult, POLAR_BUYER_TERMS_URL } from '../polar.js';

// Polar only requires the country for tax; our audience is Israeli.
const BILLING_COUNTRY = 'IL';

const GENERIC_ERROR = "משהו השתבש, נסו שוב בעוד רגע";

const POLAR_ERROR_MESSAGES = {
    AlreadyActiveSubscriptionError: "כבר יש לכם מנוי פעיל עם כתובת המייל הזו",
    PaymentError: "התשלום נדחה. נסו שוב או השתמשו בכרטיס אחר",
    ExpiredCheckoutError: "תוקף טופס התשלום פג. רעננו את הדף ונסו שוב",
    NotOpenCheckout: "תוקף טופס התשלום פג. רעננו את הדף ונסו שוב",
};

const STRIPE_APPEARANCE = {
    theme: 'night',
    variables: {
        colorPrimary: '#C5FF10',
        colorBackground: '#2B2B3C',
        colorText: '#ffffff',
        colorTextSecondary: '#b8b8c7',
        colorDanger: '#ff5c5c',
        fontFamily: '"Open Sans", sans-serif',
        fontSizeBase: '16px',
        borderRadius: '14px',
    },
    rules: {
        '.Input': { border: '1px solid #3C3C57', boxShadow: 'none' },
        '.Input:focus': { border: '1px solid #C5FF10', boxShadow: 'none' },
        '.Tab': { border: '1px solid #3C3C57', boxShadow: 'none' },
        '.Tab--selected': { border: '1px solid #C5FF10', boxShadow: 'none' },
    },
};

const STRIPE_FONTS = [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap' }];

function formatAmount(amountInMinorUnits, currency) {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: currency.toUpperCase() }).format(amountInMinorUnits / 100);
}

function formatTrial(interval, count) {
    switch (interval) {
        case 'day':
            return count === 1 ? "יום אחד" : `${count} ימים`;
        case 'week':
            return `${count * 7} ימים`;
        case 'month':
            return count === 1 ? "חודש" : `${count} חודשים`;
        case 'year':
            return count === 1 ? "שנה" : `${count} שנים`;
        default:
            return null;
    }
}

const RECURRING_LABELS = { day: "ליום", week: "לשבוע", month: "לחודש", year: "לשנה" };

// Mirrors the Stripe Elements mode Polar's own checkout form uses for each kind of session.
function getElementsMode(checkout) {
    if (checkout.is_payment_setup_required && checkout.is_payment_required && checkout.total_amount) {
        return { mode: 'subscription', setupFutureUsage: 'off_session', paymentMethodCreation: 'manual', amount: checkout.total_amount, currency: checkout.currency };
    }
    if (checkout.is_payment_required && checkout.total_amount) {
        return { mode: 'payment', paymentMethodCreation: 'manual', amount: checkout.total_amount, currency: checkout.currency };
    }
    return { mode: 'setup', paymentMethodCreation: 'manual', setupFutureUsage: 'off_session', currency: checkout.currency };
}

function Summary({ checkout }) {
    const trial = checkout.active_trial_interval && formatTrial(checkout.active_trial_interval, checkout.active_trial_interval_count);
    const recurringLabel = RECURRING_LABELS[checkout.product_price?.recurring_interval] || "";
    const price = formatAmount(checkout.total_amount, checkout.currency);
    const dueToday = formatAmount(checkout.is_payment_required ? checkout.total_amount : 0, checkout.currency);

    return (
        <div className="polar-summary">
            {trial && (
                <>
                    <p className="polar-summary-trial">{trial} ראשונים בחינם</p>
                    <p className="polar-summary-then">
                        ואחר כך {price} {recurringLabel}, החל מ-{new Date(checkout.trial_end).toLocaleDateString('he-IL')}
                    </p>
                </>
            )}
            {!trial && <p className="polar-summary-trial">{price} {recurringLabel}</p>}
            <div className="polar-summary-row">
                <span>לתשלום היום</span>
                <strong>{dueToday}</strong>
            </div>
        </div>
    );
}

function CheckoutForm({ checkout, clientSecret, email, onSuccess, onCheckoutChange }) {
    const stripe = useStripe();
    const elements = useElements();
    const [name, setName] = useState("");
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const hasTrial = !!checkout.active_trial_interval;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!stripe || !elements || processing) return;
        if (!name.trim()) {
            setError("יש להזין את השם שעל הכרטיס");
            return;
        }

        setError(null);
        setProcessing(true);
        try {
            const { error: submitError } = await elements.submit();
            if (submitError) {
                // Validation errors are already shown inline by Stripe, in Hebrew.
                if (submitError.type !== 'validation_error') setError(submitError.message);
                return;
            }

            const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({
                elements,
                params: {
                    payment_method_data: {
                        billing_details: {
                            name: name.trim(),
                            email,
                            phone: null,
                            address: { country: BILLING_COUNTRY, line1: null, line2: null, postal_code: null, city: null, state: null },
                        },
                    },
                },
            });
            if (tokenError || !confirmationToken) {
                setError(tokenError?.message || GENERIC_ERROR);
                return;
            }

            const confirmed = await confirmCheckout(clientSecret, {
                customer_name: name.trim(),
                customer_email: email,
                customer_billing_address: { country: BILLING_COUNTRY },
                confirmation_token_id: confirmationToken.id,
            });

            // 3D Secure: the bank's verification window.
            const { intent_status, intent_client_secret } = confirmed.payment_processor_metadata || {};
            if (intent_status === 'requires_action') {
                const { error: actionError } = await stripe.handleNextAction({ clientSecret: intent_client_secret });
                if (actionError) {
                    setError(actionError.message);
                    return;
                }
            }

            const status = await waitForCheckoutResult(clientSecret);
            if (status === 'failed' || status === 'expired') {
                setError("התשלום לא הושלם. נסו שוב או השתמשו בכרטיס אחר");
                return;
            }
            // 'confirmed' after the timeout means Polar is still processing; the webhook is the source of truth.
            onSuccess();
        } catch (err) {
            console.error('Polar checkout failed:', err);
            if (err.code === 'TrialAlreadyRedeemed') {
                // Same as Polar's own form: drop the trial and let the customer pay the full price.
                try {
                    onCheckoutChange(await updateCheckout(clientSecret, { allow_trial: false }));
                    setError("כבר השתמשתם בתקופת הניסיון, ולכן החיוב יתחיל היום");
                } catch (updateErr) {
                    console.error('Polar checkout update failed:', updateErr);
                    setError(GENERIC_ERROR);
                }
                return;
            }
            setError(POLAR_ERROR_MESSAGES[err.code] || GENERIC_ERROR);
        } finally {
            setProcessing(false);
        }
    }

    return (
        <form className="polar-checkout" onSubmit={handleSubmit}>
            <label className="polar-label" htmlFor="polar-cardholder">שם בעל/ת הכרטיס</label>
            <input
                id="polar-cardholder"
                className="polar-input"
                type="text"
                autoComplete="cc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
            />

            <PaymentElement
                options={{
                    layout: 'tabs',
                    wallets: { applePay: 'never', googlePay: 'never' },
                    // Our own Hebrew authorization text below covers the card mandate.
                    terms: { card: 'never' },
                    fields: { billingDetails: { name: 'never', email: 'never', phone: 'never', address: 'never' } },
                }}
            />

            <Summary checkout={checkout} />

            {error && <p className="polar-error">{error}</p>}

            <button className="button-cta polar-submit" type="submit" disabled={!stripe || processing}>
                {processing ? "מעבדים את התשלום..." : hasTrial ? "התחילו את תקופת הניסיון" : "לתשלום"}
            </button>

            <p className="polar-legal">
                בלחיצה על "{hasTrial ? "התחילו את תקופת הניסיון" : "לתשלום"}" אתם מאשרים ל-<bdi>Polar Software, Inc.</bdi>, המשווק המקוון שלנו
                (<bdi>Merchant of Record</bdi>), לחייב את אמצעי התשלום שבחרתם בסכום המוצג
                {hasTrial ? " בתום תקופת הניסיון" : ""} ובכל מועד חיוב לאחר מכן עד לביטול, ומסכימים
                ל<a href={POLAR_BUYER_TERMS_URL} target="_blank" rel="noreferrer">תנאי הרכישה</a>.
                {hasTrial ? " אפשר לבטל בכל עת לפני סוף תקופת הניסיון ולא תחויבו." : ""}
            </p>
        </form>
    );
}

export function PolarCheckout({ email, onSuccess }) {
    const [session, setSession] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoadError(false);
        createCheckoutSession(email)
            .then((clientSecret) => getCheckout(clientSecret).then((checkout) => ({ clientSecret, checkout })))
            .then((result) => {
                if (!cancelled) setSession(result);
            })
            .catch((err) => {
                console.error('Failed to load Polar checkout:', err);
                if (!cancelled) setLoadError(true);
            });
        return () => { cancelled = true; };
    }, [email, attempt]);

    const publishableKey = session?.checkout.payment_processor_metadata.publishable_key;
    const stripePromise = useMemo(() => publishableKey && loadStripe(publishableKey), [publishableKey]);

    if (loadError) {
        return (
            <div className="polar-checkout">
                <p className="polar-error">לא הצלחנו לטעון את טופס התשלום</p>
                <button className="button-cta polar-submit" type="button" onClick={() => setAttempt(attempt + 1)}>נסו שוב</button>
            </div>
        );
    }

    if (!session) {
        return <div className="polar-checkout polar-loading">טוענים את טופס התשלום...</div>;
    }

    return (
        <Elements
            stripe={stripePromise}
            options={{
                ...getElementsMode(session.checkout),
                locale: 'he',
                appearance: STRIPE_APPEARANCE,
                fonts: STRIPE_FONTS,
            }}
        >
            <CheckoutForm
                checkout={session.checkout}
                clientSecret={session.clientSecret}
                email={email}
                onSuccess={onSuccess}
                onCheckoutChange={(checkout) => setSession({ ...session, checkout })}
            />
        </Elements>
    );
}

export default PolarCheckout
